import { useEffect, useState, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import { STATUS_SEGMENTS, type StatusSegment, type UsersFilters } from '../use-url-filters';

interface Props {
  filters: UsersFilters;
  onQuery: (q: string) => void;
  onSegment: (s: StatusSegment) => void;
}

/** Búsqueda por nombre o correo (debounce 300 ms hacia la URL) + estado. */
export function UsersFilterBar({ filters, onQuery, onSegment }: Props): ReactElement {
  const [text, setText] = useState(filters.q);

  // La URL manda: un "Limpiar filtros" o un deep-link reemplazan el texto escrito.
  useEffect(() => {
    setText(filters.q);
  }, [filters.q]);

  useEffect(() => {
    if (text === filters.q) {
      return;
    }
    const id = setTimeout(() => onQuery(text), 300);
    return () => clearTimeout(id);
  }, [text, filters.q, onQuery]);

  return (
    <div className="filters">
      <label className="search">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Buscar por nombre o correo…"
          aria-label="Buscar usuarios"
          maxLength={100}
        />
      </label>

      <div className="seg" role="group" aria-label="Filtrar por estado">
        {STATUS_SEGMENTS.map((s) => (
          <button
            key={s.value}
            type="button"
            className={cn(filters.segment === s.value && 'on')}
            aria-pressed={filters.segment === s.value}
            onClick={() => onSegment(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
